import { useState } from '@/composables/common/useState';
import { useFetch } from '@/composables/fetch/useFetch';
import { FilteredItemsResponse, FiltersService } from '@/types/service/filters.service';
import { watch } from 'vue';

interface FiltersOptions<T> {
  initialFilters?: T;
  watcher?: boolean;
}
type FunctionFetchFilteredItemsType<F, T> = (filters?: F) => Promise<T[]>;

export const useFilters = <T, F, A = {}>(
  service: FiltersService<T, F, A>,
  options?: FiltersOptions<F>
) => {
  const initialFilters = options?.initialFilters ?? null;
  const [filters, setFilters] = useState<F | null>(initialFilters);
  const [totalItems, setTotalItems] = useState(0);
  const [result, setResult] = useState<FilteredItemsResponse<T, A>>(
    {} as FilteredItemsResponse<T, A>
  );

  const {
    data: filteredItems,
    callFetch: fetchFilteredItems,
    loading: isFetchFilteredItems,
  } = useFetch<T[], FunctionFetchFilteredItemsType<F, T>>(async (argFilters) => {
    const { data } = await service.getFilteredItems(argFilters ?? filters.value);
    setTotalItems(data?.count || 0);
    setResult(data || 0);
    return data?.data;
  }, []);

  const resetFilters = () => setFilters(initialFilters);

  if (options?.watcher) {
    watch(
      filters,
      (newFilters) => {
        fetchFilteredItems();
      },
      { deep: true }
    );
  }

  return {
    filters,
    setFilters,
    resetFilters,

    totalItems,
    result,
    filteredItems,
    fetchFilteredItems,
    isFetchFilteredItems,
  };
};
